import type { ReactNode } from 'react';

import Button from 'components/Button';
import Icon, { type IconName } from 'components/Icon';
import Message from 'components/Message';
import { PriceText } from 'components/Price';
import { ShelfInformation, StockStatus } from 'components/Stock';
import Text from 'components/Text';
import type { FormattedPrice, Location } from 'models/api';
import type { VariantId } from 'models/product';
import type { ColorString } from 'types';
import { is } from 'utils/helpers';

export interface DeliveryMethod {
	description: string | undefined;
	price: FormattedPrice;
	title: string | undefined;
}

interface BaseProps {
	/** Text for the button (e.g. store changer, watchable signup) */
	buttonText?: string;
	/** Stock location header */
	header: string;
	/** General info about stock */
	infoText?: ReactNode;
	inStock: boolean;
	/** Stock location icon */
	locationIcon: IconName;
	/** Click handler for the button (e.g. store changer, watchable signup) */
	onClick?: (() => void) | undefined;

	openingHours?: string | undefined;
	/** Primary status text next to the icon*/
	statusText: string;

	stockInformation?: string;
	/** Number in web stock for third party scripts */
	webStockAmount?: number;
}

interface WithShelfInformation extends BaseProps {
	articleNumber: VariantId;
	categoryColor: ColorString;
	categoryName: string;
	deliveryMethods?: never;
	shelfLocations: Location[] | undefined;
}

interface WithDeliveryMethods extends BaseProps {
	articleNumber?: never;
	categoryColor?: never;
	categoryName?: never;
	deliveryMethods: DeliveryMethod[] | undefined;
	shelfLocations?: never;
}

type Props = WithShelfInformation | WithDeliveryMethods;

export default function StockBalanceListRow({
	articleNumber,
	buttonText,
	categoryColor,
	categoryName,
	deliveryMethods,
	header,
	infoText,
	inStock,
	locationIcon,
	onClick,
	openingHours,
	shelfLocations,
	statusText,
	stockInformation,
	webStockAmount,
}: Props) {
	return (
		<div className="p-4" data-web-stock={webStockAmount}>
			<div className="flex flex-wrap justify-between">
				<p className="flex items-center font-bold">
					<Icon name={locationIcon} className="mr-2" />
					{header}
				</p>
				{openingHours && <p className="pl-8">{openingHours}</p>}
			</div>
			<div className="mt-3 pl-8">
				<StockStatus inStock={inStock} text={statusText} />
				{stockInformation && (
					<Text as="pSmall" text={stockInformation} className="mt-2" />
				)}
				{inStock && (
					<>
						{is.arrayWithLength(deliveryMethods) && (
							<div className="mt-3 space-y-2">
								{deliveryMethods.map((method) => (
									<div key={`${method.title}-${method.description}`}>
										<Text
											className="font-bold"
											as="pSmall"
											text={method.title}
										/>
										<div className="flex justify-between">
											<Text as="pSmall">{method.description}</Text>
											<PriceText
												price={method.price}
												className="text-sm font-bold"
											/>
										</div>
									</div>
								))}
							</div>
						)}
						<ShelfInformation
							articleNumber={articleNumber}
							className="mt-4"
							categoryColor={categoryColor}
							categoryName={categoryName}
							locations={shelfLocations}
						/>
					</>
				)}
				{onClick && (
					<Button
						className="mt-4"
						variant="text"
						onClick={onClick}
						aria-haspopup="dialog"
						size="sm"
					>
						{buttonText}
					</Button>
				)}
			</div>
			{infoText && (
				<Message variant="information" className="mt-4">
					{infoText}
				</Message>
			)}
		</div>
	);
}
StockBalanceListRow.displayName = 'StockBalanceListRow';
